import React, { useEffect, useMemo, useState } from "react";
import * as topojson from "topojson-client";
import { geoNaturalEarth1, geoPath } from "d3-geo";
import { motion, AnimatePresence } from "framer-motion";
import { Globe2, X, ShieldAlert, FileWarning, MapPin } from "lucide-react";
import { Panel, StatTile } from "@/components/common/Panel";
import { ModuleGate } from "@/components/common/ModuleGate";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import worldTopo from "@/assets/world-110m.json";

const WIDTH = 960;
const HEIGHT = 500;

const countries = topojson.feature(worldTopo, worldTopo.objects.countries).features;
const projection = geoNaturalEarth1().fitSize([WIDTH, HEIGHT], { type: "FeatureCollection", features: countries });
const pathGen = geoPath(projection);

function relevanceTone(total) {
    if (total >= 20) return { fill: "var(--neon-red)", glow: "rgb(var(--accent-3-rgb) / 0.6)" };
    if (total >= 5) return { fill: "var(--warning)", glow: "rgba(255,131,43,0.5)" };
    if (total > 0) return { fill: "var(--neon-cyan)", glow: "rgb(var(--accent-1-rgb) / 0.55)" };
    return { fill: "#64748B", glow: "rgba(100,116,139,0.35)" };
}

export default function GovernanceMap() {
    const [frameworks, setFrameworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);
    const [hovered, setHovered] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/governance/frameworks-summary");
                setFrameworks(data.frameworks);
            } catch (e) {
                console.warn("Failed to load frameworks", e);
            } finally { setLoading(false); }
        })();
    }, []);

    const totals = useMemo(() => ({
        frameworks: frameworks.length,
        findings: frameworks.reduce((s, f) => s + f.relevant_findings, 0),
        conflicts: frameworks.reduce((s, f) => s + f.relevant_sod_conflicts, 0),
        highest: frameworks.slice().sort((a, b) => b.total_relevance - a.total_relevance)[0]?.code ?? "—",
    }), [frameworks]);

    return (
        <ModuleGate module="governance" label="Governance — Compliance Map">
        <div className="space-y-6">
            <div className="panel-elevated relative overflow-hidden p-6">
                <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
                <div className="flex items-center gap-4 relative">
                    <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-cyan-300 to-cyan-600 flex items-center justify-center shadow-[0_0_20px_rgb(var(--accent-1-rgb) / 0.45)]">
                        <Globe2 size={22} className="text-slate-950" strokeWidth={2.5} />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl text-white tracking-wide">Global Compliance Map</h1>
                        <p className="text-sm text-slate-400 mt-0.5">
                            Governance frameworks by geography — live cross-referenced against your Findings &amp; SoD conflicts
                        </p>
                    </div>
                </div>
            </div>

            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatTile testId={TID.statTile("gov-frameworks")} label="Frameworks Tracked" value={totals.frameworks} sub="global regulations" icon={Globe2} tone="cyan" />
                <StatTile testId={TID.statTile("gov-findings")} label="Relevant Findings" value={totals.findings} sub="across workspaces" icon={FileWarning} tone="amber" />
                <StatTile testId={TID.statTile("gov-conflicts")} label="Relevant SoD Conflicts" value={totals.conflicts} sub="across role library" icon={ShieldAlert} tone="red" />
                <StatTile testId={TID.statTile("gov-highest")} label="Highest Exposure" value={totals.highest} sub="most relevant framework" tone="slate" icon={MapPin} />
            </motion.div>

            <Panel eyebrow="Interactive Map" title="Click a pointer to view framework details">
                <div className="relative">
                    <svg
                        data-testid={TID.complianceMapCanvas}
                        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                        className="w-full h-auto"
                        style={{ background: "radial-gradient(ellipse at center, var(--bg-elevated) 0%, #05050A 80%)" }}
                    >
                        <g>
                            {countries.map((c, idx) => (
                                <path key={c.id ?? idx} d={pathGen(c)} fill="#161A28" stroke="#2A3550" strokeWidth={0.5} />
                            ))}
                        </g>
                        {!loading && frameworks.map((fw) => {
                            const [x, y] = projection([fw.lng, fw.lat]);
                            const tone = relevanceTone(fw.total_relevance);
                            const isActive = selected?.code === fw.code || hovered?.code === fw.code;
                            return (
                                <g
                                    key={fw.code}
                                    data-testid={TID.complianceMapPin(fw.code)}
                                    transform={`translate(${x},${y})`}
                                    onClick={() => setSelected(fw)}
                                    onMouseEnter={() => setHovered(fw)}
                                    onMouseLeave={() => setHovered(null)}
                                    className="cursor-pointer"
                                >
                                    <circle r={isActive ? 10 : 7} fill={tone.fill} opacity={0.25} style={{ filter: `drop-shadow(0 0 6px ${tone.glow})` }} />
                                    <circle r={isActive ? 5.5 : 4} fill={tone.fill} stroke="#05050A" strokeWidth={1.2} />
                                    {isActive && (
                                        <text y={-14} textAnchor="middle" className="font-mono" fontSize="11" fill="#F2F6FB">
                                            {fw.code}
                                        </text>
                                    )}
                                </g>
                            );
                        })}
                    </svg>

                    <AnimatePresence>
                        {selected && (
                            <motion.div
                                data-testid={TID.complianceMapDetail}
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                className="absolute top-2 right-2 w-full sm:w-[340px] panel-elevated p-4 max-h-[440px] overflow-y-auto"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="label-eyebrow">{selected.region}</div>
                                        <h3 className="font-display text-lg text-white mt-0.5">{selected.name}</h3>
                                        <div className="text-[10px] font-mono text-cyan-400 mt-0.5">{selected.code}</div>
                                    </div>
                                    <button onClick={() => setSelected(null)} className="text-slate-500 hover:text-white transition-colors" aria-label="Close">
                                        <X size={16} />
                                    </button>
                                </div>
                                <p className="text-xs text-slate-400 mt-3 leading-relaxed">{selected.description}</p>
                                <div className="flex items-center gap-2 mt-3">
                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30">
                                        {selected.relevant_findings} relevant findings
                                    </span>
                                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-rose-500/15 text-rose-300 border border-rose-500/30">
                                        {selected.relevant_sod_conflicts} SoD conflicts
                                    </span>
                                </div>
                                <div className="label-eyebrow mt-4 mb-1">Key Requirements</div>
                                <ul className="text-[11px] text-slate-300 list-disc pl-4 space-y-1">
                                    {selected.key_requirements.map((k, i) => <li key={i}>{k}</li>)}
                                </ul>
                                <div className="label-eyebrow mt-4 mb-1">Tag Vocabulary</div>
                                <div className="flex flex-wrap gap-1">
                                    {selected.tags.map((t) => (
                                        <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-slate-300 border border-white/10">{t}</span>
                                    ))}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
                <p className="text-[11px] text-slate-500 font-mono mt-3">
                    Pointer size/color reflects total relevance (findings + SoD conflicts tagged to that framework&apos;s control domain) — recomputed live from your data.
                </p>
            </Panel>
        </div>
        </ModuleGate>
    );
}
